import { asyncChain, asyncChainIt, asyncChannel, asyncCollect, asyncFilter, asyncFlatMap, asyncForEach, asyncIter, asyncMap, iterCallback, iterPromise, } from './asyncIter.js';
/**
 * A wrapper around async iterables that also exposes the async utility functions from this library as methods
 *
 * @category helpers
 */
export class AsyncIterWrapper {
    #impl;
    /**
     * Wrap an existing async iterable
     *
     * @category wrapping
     * @see {@link AsyncIterWrapper.wrap} - the function to use instead of constructing manually
     */
    constructor(iterable) {
        this.#impl = iterable;
    }
    /**
     * Implement the async iterable protocol
     */
    [Symbol.asyncIterator]() {
        return this.#impl[Symbol.asyncIterator].call(this.#impl);
    }
    /**
     * Wrap an ordinary {@link Iterable} or async iterable
     *
     * @category wrapping
     * @see {@link iterPromise}
     */
    static wrap(
    /**
     * The iterable to wrap
     */
    iterable) {
        if (iterable instanceof AsyncIterWrapper) {
            return iterable;
        }
        else if (Symbol.asyncIterator in iterable) {
            return new AsyncIterWrapper(iterable);
        }
        else {
            return new AsyncIterWrapper(iterPromise(Promise.resolve(iterable)));
        }
    }
    /**
     * Create an {@link AsyncChannel}
     *
     * @category creators
     */
    static channel() {
        const channel = asyncChannel();
        return [channel, AsyncIterWrapper.wrap(channel.iter)];
    }
    /**
     * Iterate over an iterator
     *
     * @category creators
     */
    static iter(
    /**
     * The iterator to iterate over
     */
    it) {
        return AsyncIterWrapper.wrap(asyncIter(it));
    }
    /**
     * Iterate over the values passed to a callback and optionally the function return value
     *
     * @category creators
     * @see {@link iterCallback}
     */
    static iterCallback(
    /**
     * The function to call and watch the callback
     */
    fn, options) {
        if (options?.return) {
            return AsyncIterWrapper.wrap(iterCallback(fn, { return: true }));
        }
        else {
            return AsyncIterWrapper.wrap(iterCallback(fn, {
                return: false,
            }));
        }
    }
    filter(pred) {
        return AsyncIterWrapper.wrap(asyncFilter(this.#impl, pred));
    }
    /**
     * Returns a new iterable containing the mapped values of the given iterable, by the given transform
     *
     * @category transformers
     * @see {@link asyncMap}
     */
    map(
    /**
     * The transform to use
     */
    map) {
        return AsyncIterWrapper.wrap(asyncMap(this.#impl, map));
    }
    /**
     * Return an iterable containing the values of every iterable returned by calling the map on the provided iterable
     *
     * @category transformers
     * @see {@link asyncFlatMap}
     */
    flatMap(
    /**
     * The transform function, returning iterables
     */
    map) {
        return AsyncIterWrapper.wrap(asyncFlatMap(this.#impl, map));
    }
    /**
     * Returns a new iterable containing the values of each iterable, one iterable after the other
     *
     * Version with a vararg of iterables
     *
     * @category aggregators
     * @see {@link asyncChain}
     */
    static chain(...iters) {
        if (iters.length === 1)
            return AsyncIterWrapper.wrap(iters[0]);
        else
            return AsyncIterWrapper.wrap(asyncChain(...iters));
    }
    /**
     * Returns a new iterable containing the values of each iterable, one iterable after the other
     *
     * Version with an iterable of iterables
     *
     * @category aggregators
     * @see {@link asyncChainIt}
     */
    static chainIt(iters) {
        return AsyncIterWrapper.wrap(asyncChainIt(iters));
    }
    /**
     * Iterate over the entire iterable and run a function for every item
     *
     * @category collection
     * @see {@link asyncForEach}
     */
    async forEach(
    /**
     * The function to run on every item
     */
    fn) {
        await asyncForEach(this.#impl, fn);
    }
    /**
     * Iterate over the entire iterable and collect the values into an array
     *
     * @category collection
     * @see {@link asyncCollect}
     */
    async collect() {
        return asyncCollect(this.#impl);
    }
}
//# sourceMappingURL=data:application/json;base64,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